const delay = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

export function resolveApiBase() {
  const configuredUrl = import.meta.env.VITE_TRANSCRIPTION_API_URL?.trim();
  if (!configuredUrl || configuredUrl === '/') return '';

  let parsedUrl;
  try {
    parsedUrl = new URL(configuredUrl);
  } catch {
    throw new Error('The configured transcription service URL is invalid.');
  }

  if (import.meta.env.PROD && ['localhost', '127.0.0.1', '[::1]'].includes(parsedUrl.hostname)) {
    throw new Error(
      'The production transcription URL points to localhost. Configure VITE_TRANSCRIPTION_API_URL with the deployed CutScript service URL.'
    );
  }
  if (!['https:', 'http:'].includes(parsedUrl.protocol) || parsedUrl.username || parsedUrl.password || parsedUrl.search || parsedUrl.hash || (import.meta.env.PROD && parsedUrl.protocol !== 'https:')) {
    throw new Error('Use an HTTPS transcription service URL without credentials, query parameters or fragments.');
  }
  return configuredUrl.replace(/\/+$/, '');
}

async function request(url, options, signal, getAccessToken) {
  const accessToken = await getAccessToken();
  if (!accessToken) throw new Error('Your session ended. Sign in again to transcribe.');
  try {
    return await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`
      },
      signal: AbortSignal.any([signal || new AbortController().signal, AbortSignal.timeout(options.method === 'POST' ? 5 * 60_000 : 30_000)])
    });
  } catch (error) {
    if (signal?.aborted || error.name === 'AbortError') throw error;
    if (error.name === 'TimeoutError') throw new Error('The transcription service took too long to respond. Please retry.');
    if (error instanceof TypeError) {
      const origin = new URL(url, window.location.href).origin;
      throw new Error(
        `Could not reach the transcription service at ${origin}. The endpoint may be unavailable or blocked by CORS; no HTTP response was received.`
      );
    }
    throw new Error('The transcription request could not be completed.');
  }
}

async function readResponse(response) {
  const result = await response.json().catch(() => ({}));
  if (response.ok) {
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new Error('This address serves the editor but not its transcription API. Deploy the Node server or configure VITE_TRANSCRIPTION_API_URL.');
    }
    return result;
  }
  if (response.status === 401 || response.status === 403) {
    throw new Error(result.error || 'The transcription service rejected this session or CutScript access is not enabled.');
  }
  if (response.status === 404) {
    throw new Error(result.error || 'The transcription endpoint was not found. Check the configured service URL and API route.');
  }
  if (response.status === 413) {
    throw new Error(result.error || 'The transcription service rejected the upload size.');
  }
  if (response.status === 409) {
    throw new Error(result.error || 'A transcription is already running. Wait for it to finish.');
  }
  if (response.status === 429) {
    throw new Error(result.error || 'The transcription service is busy. Please retry later.');
  }
  throw new Error(result.error || `The transcription service returned HTTP ${response.status}.`);
}

export async function transcribeVideo(file, {signal, onState, getAccessToken}) {
  if (!getAccessToken) throw new Error('Sign in to your CutScript test account before transcription.');
  const baseUrl = resolveApiBase();
  await readResponse(await request(`${baseUrl}/api/status`, {method: 'GET'}, signal, getAccessToken));
  const form = new FormData();
  form.append('video', file);
  onState?.('Uploading video and starting transcription…');

  const startResponse = await request(
    `${baseUrl}/api/transcriptions`,
    {method: 'POST', body: form},
    signal,
    getAccessToken
  );
  const job = await readResponse(startResponse);
  if (!job.jobId) throw new Error('The server did not return a transcription job ID.');
  onState?.('Video received · transcription running…');

  const deadline = Date.now() + 25 * 60_000;
  while (Date.now() < deadline) {
    await delay(1800);
    if (signal?.aborted) throw new DOMException('Transcription canceled.', 'AbortError');
    const response = await request(
      `${baseUrl}/api/transcriptions/${encodeURIComponent(job.jobId)}`,
      {method: 'GET'},
      signal,
      getAccessToken
    );
    const result = await readResponse(response);
    if (result.status === 'completed') return result;
    if (result.stage === 'aligning') onState?.('Transcript complete · aligning word timing…');
    if (result.status === 'failed') {
      throw new Error(result.error || 'Transcription failed. Please retry.');
    }
    if (result.status !== 'processing') {
      throw new Error('The server returned an unknown transcription job state.');
    }
  }
  throw new Error('Transcription exceeded the waiting limit. Please retry later.');
}


// Submit only the cloud project ID. AssemblyAI downloads private media directly
// from a short-lived signed Supabase URL, avoiding large uploads through Render.
export async function transcribeCloudVideo(projectId,{signal,onState,getAccessToken,force=false}={}){
 if(!projectId||!getAccessToken)throw new Error('Save and sign in before transcribing.');
 const baseUrl=resolveApiBase();
 onState?.('Starting cloud transcription…');
 const started=await readResponse(await request(baseUrl+'/api/cloud-transcriptions',{
  method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({projectId,force})
 },signal,getAccessToken));
 if(!started.jobId)throw new Error('No durable transcription job was created.');
 onState?.(started.resumed?'Resuming existing transcription…':'Cloud transcription running · safe to leave and return…');
 const deadline=Date.now()+4*60*60*1000;
 while(Date.now()<deadline){
  if(signal?.aborted)throw new DOMException('Monitoring stopped; cloud transcription continues.','AbortError');
  const result=await readResponse(await request(baseUrl+'/api/cloud-transcriptions/'+encodeURIComponent(started.jobId),{method:'GET'},signal,getAccessToken));
  if(result.status==='completed')return result;
  if(result.status==='failed')throw new Error(result.error||'Cloud transcription failed.');
  onState?.('AssemblyAI processing · job saved, safe to return later…');
  await delay(5000);
 }
 throw new Error('Transcription is still processing. Return to this project and click Transcribe to resume.');
}
